import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import PreferencesMediumButton from "components/ProjectSpecificComponents/Elements/Global/atoms/Buttons/PreferencesMediumButton"
import SepLine from "components/ProjectSpecificComponents/Elements/Global/atoms/SepLine"
import { INewRequestFormField } from "components/ProjectSpecificComponents/Interfaces/FormsInterface"

import { defaultFieldData } from "components/ProjectSpecificComponents/Stores/FormStore/defaults"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import UserSideNewRequestFormInputField from "../UserSideNewRequestFormInputField"
import {
	isReactSelectSingleOption,
	TReactSelectOption,
	TReactSelectSelectedOptions,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import { observer } from "mobx-react-lite"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import { FormInput } from "components/ProjectNonSpecificComponents/Elements/FormElements/FormInput"
import i18n from "i18n"
import parse from "html-react-parser"

interface Props {
	config: INewRequestFormField[]
	submitDisabled?: boolean
	/**
	 * `formData` is dynamic and based on `config` prop
	 */
	onSubmitForm?: TOnSubmitNewRequestForm
	onChangeForm?: TOnSubmitNewRequestForm
	triggerClearValues?: boolean
	resetTriggerClearValues?: () => void
}
export interface IFormVal {
	[k: string]: string | string[] | TReactSelectSelectedOptions
}

const UserSideNewRequestForm = observer(
	({
		config,
		submitDisabled,
		onSubmitForm = () => {},
		onChangeForm = () => {},
		triggerClearValues = false,
		resetTriggerClearValues = () => {},
	}: Props) => {
		const { isMobile } = useScreenType()
		const { t } = useTranslation()
		const [Form] = useState<IFormVal>({})
		const formStore = useFormStore()
		const [, refresh] = useState(0)
		const [cookie, setCookie] = useState<boolean>(false)
		const [addedFiles, setAddedFiles] = useState<File[]>([])
		const { userSideAttachments } = formStore.settingsData.additionalInfo.toolTipTexts
		const { cookieBar } = formStore.settingsData.additionalInfo
		const { userSelectedForm } = formStore
		const isEN = i18n.language === "en"

		const setFormValue = (
			fieldKey: string,
			newValue: string | readonly TReactSelectOption[] | TReactSelectOption
		) => {
			if (isReactSelectSingleOption(newValue)) {
				let selectedField = config?.find(({ mainFieldKey }) => mainFieldKey == fieldKey)

				if (selectedField?.itemOfTypeUserFieldId) {
					Form["USER_SETTABLE"] = newValue?.userProp ?? ""
				}
				Form[fieldKey] = newValue.value
			} else {
				Form[fieldKey] = newValue
			}
			onChangeForm(Form, addedFiles)
			refresh((count) => count + 1)
		}

		const getFormValue = (fieldKey: string) => Form[fieldKey] ?? undefined

		const clearFormValues = () => {
			formStore.setUserFormItemOfTypeLevels("frstLvlValue", undefined)
			formStore.setUserFormItemOfTypeLevels("scndLvlValue", undefined)
			formStore.setUserFormItemOfTypeLevels("thrdLvlValue", undefined)
			formStore.setUserFormItemOfTypeLevels("scndLvlOptions", undefined)
			formStore.setUserFormItemOfTypeLevels("thrdLvlOptions", undefined)

			for (let fieldKey in Form) {
				delete Form[fieldKey]
			}

			setAddedFiles([])
			onChangeForm(Form, addedFiles)
			refresh((count) => count + 1)
		}

		useEffect(() => {
			if (triggerClearValues) {
				clearFormValues()
				resetTriggerClearValues()
			}
		}, [triggerClearValues])

		const picklistIdFromCollection = getFormValue(
			userSelectedForm?.collections[0].picklistFieldId ?? ""
		)

		useEffect(() => {
			setCookie(false)
			if (!cookieBar) return
			let _modal = !!cookieBar?.isModal
			if (_modal) return
			let _emptyInfo = isEN ? !!cookieBar?.textEN : !!cookieBar?.textLV
			if (!_emptyInfo) return

			setCookie(true)
		}, [cookieBar, isEN])

		return (
			<>
				<HyperDiv direction="column">
					<HyperDiv direction="column">
						{config
							.map((data, i) => {
								let { isConditional, mainFieldKey, predicateFieldKey, predicateValues } = data
								// There are two types of field that can be used as predicateFieldKey - picklist and multi_picklist
								// multi_picklist values are in array and picklist value is string
								const isFieldShown =
									isConditional && predicateFieldKey && predicateValues
										? Array.isArray(Form[predicateFieldKey])
											? predicateValues.some(({ value: predicateValue }) => {
													let multiPicklistArray = Form[predicateFieldKey] as TReactSelectOption[]
													return multiPicklistArray?.some(({ value }) => value == predicateValue)
											  })
											: predicateValues.some(({ value }) => value == Form[predicateFieldKey])
										: true

								if (!isFieldShown) return <div key={mainFieldKey + i}></div>

								return (
									<div key={mainFieldKey + i}>
										<UserSideNewRequestFormInputField
											config={data}
											picklistIdFromCollection={picklistIdFromCollection}
											value={getFormValue(mainFieldKey)}
											onChangeValue={(value, customKey) => {
												setFormValue(customKey ?? mainFieldKey, value)
											}}
										/>
										<Whitespace x={`${isMobile ? "1" : "2"}`} />
									</div>
								)
							})
							.concat([
								// Concat is used to keep all HyperDiv's children in one array,
								// so that `spaceBetweenElems` functions as intended.

								<div key={"attachment"} className="flex flex-row items-center">
									<UserSideNewRequestFormInputField
										config={{
											...defaultFieldData.newRequestForm,
											fieldType: "ATTACHMENT",
											nameEN: t("rawText.attachments"),
											nameLV: t("rawText.attachments"),
											tooltip: {
												textLV: userSideAttachments?.textLV ?? "",
												textEN: userSideAttachments?.textEN ?? "",
											},
										}}
										files={addedFiles}
										onChangeFiles={setAddedFiles}
										resizeForTooltip={!!userSideAttachments.textEN || !!userSideAttachments.textLV}
									/>
								</div>,
							])}
					</HyperDiv>

					<SepLine margin={isMobile ? ".5rem 0" : "2rem 0"} />
					<div className="font-size-14">
						{"* "}
						{t("forms.tooltip.asterixIsRequiredNotice")}
					</div>
					<Whitespace x="1" />
					{cookie && (
						<>
							<div className="font-size-14">
								{"** "}
								{cookieBar?.isRichText
									? isEN
										? parse(String(cookieBar?.textEN))
										: parse(String(cookieBar?.textLV))
									: isEN
									? cookieBar?.textEN
									: cookieBar?.textLV}
							</div>
							<Whitespace x="2" />
						</>
					)}

					<HyperDiv centeredX>
						<PreferencesMediumButton
							style={{
								width: isMobile ? "100%" : undefined,
							}}
							disabled={submitDisabled}
							onClick={() => onSubmitForm(Form, addedFiles)}
							children={t("button.submit")}
						/>
					</HyperDiv>
				</HyperDiv>
				{/* Honeypot captcha method */}
				{/* Hidden fields to attract bots like flies to honey */}
				<div
					key={"hd-name"}
					className="opacity-0 fixed left-0 top-0 h-0 w-0 "
					style={{ zIndex: -1 }}
				>
					<FormInput
						wide
						defaultValue=""
						autoComplete="off"
						onValueChange={(text) => setFormValue("hd-name", text)}
						name="hd-name"
					/>
				</div>
				<div
					key={"hd-password"}
					className="opacity-0 fixed left-0 top-0 h-0 w-0 "
					style={{ zIndex: -1 }}
				>
					<FormInput
						wide
						defaultValue=""
						autoComplete="off"
						onValueChange={(text) => setFormValue("hd-password", text)}
						name="hd-password"
					/>
				</div>
			</>
		)
	}
)

export default UserSideNewRequestForm

export type TOnSubmitNewRequestForm = (formData: IFormVal, files: File[]) => void
